import React, { useCallback, useEffect, useRef } from "react";
import Login from "./components/Login";

import { Toaster } from "react-hot-toast";

import { useAuthContext } from "./context/auth";
import PortraitDefault from "./components/PortraitDefault";
import LandscapeDefault from "./components/LandscapeDefault";
import IceBreaking from "./components/Programs/IceBreaking/IceBreaking";
import DynamicStretching from "./components/Programs/DynamicStretching/DynamicStretching";
import LandscapeDemo from "./components/Programs/Demo/LandscapeDemo";
import PortraitDemoA from "./components/Programs/Demo/PortraitDemoA";
import WorkoutType1 from "./components/Programs/Workout/WorkoutType1";
import WorkoutType2 from "./components/Programs/Workout/WorkoutType2";
import LandscapeTeamWorkout from "./components/Programs/TeamWorkout/LandscapeTeamWorkout";
import Admin from "./components/Admin/Admin";
import Start from "./components/Programs/Start/Start";
import Loading from "./components/Loading";
import PortraitDemoB from "./components/Programs/Demo/PortraitDemoB";
import Rest from "./components/Rest";
import PortraitTeamWorkout from "./components/Programs/TeamWorkout/PortraitTeamWorkout";
import CoolDown from "./components/Programs/CoolDown/CoolDown";
import ClubDay from "./components/Programs/ClubDay/ClubDay";
import LoadingPortrait from "./components/LoadingPortrait";
import WorkoutType3 from "./components/Programs/Workout/WorkoutType3";
import LandscapeRow from "./components/Programs/Row/LandscapeRow";
import PortraitRow from "./components/Programs/Row/PortraitRow";
import LandscapeTeamRow from "./components/Programs/Row/LandscapeTeam";
import LandscapeRowTeamCheck from "./components/Programs/Row/LandscapeTeamCheck";
import BasicScreen from "./components/Programs/Basic/BasicScreen";
import ControlPanel from "./components/ControlPanel";
import LandescapeScreen from "./components/Programs/Basic/LandescapeScreen";
import HiluxTeamSetup from "./components/Programs/Hilux/HiluxTeamSetup";

import HiluxDifficulty from "./components/Programs/Hilux/HiluxDifficulty";
import HiluxDifficultyCheck from "./components/Programs/Hilux/HiluxDifficultyCheck";
import HiluxWorkout from "./components/Programs/Hilux/HiluxWorkout";
import HiluxStationPortrait from "./components/Programs/Hilux/HiluxStationPortrait";
import HiluxRanking from "./components/Programs/Hilux/HiluxRanking";
import HiluxPortraitDefault from "./components/Programs/Hilux/HiluxPortraitDefault";
import useClub from "./hooks/useClub";

function App() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    isAuthenticated,
    loading,
    did,
    step,
    circuitProgram,
    weightProgram,
    basicProgram,
    hiluxProgram,
    setStep,
    row,
    classId,
  } = useAuthContext();
  const clubDayBadges = useClub();

  const resizeContent = useCallback(() => {
    if (!ref.current) return;

    const scale = Math.min(1, window.innerWidth / 1920);
    const containerHeight = window.innerHeight;
    const scaledHeight = 1080 * scale;

    const yOffset = Math.max(0, (containerHeight - scaledHeight) / 2);

    // transform으로 스케일 및 세로 위치 조정
    ref.current.style.transform = `scale(${scale}) translateY(${
      yOffset / scale
    }px)`;
    ref.current.style.transformOrigin = "top left";
  }, []);

  useEffect(() => {
    if (did === "0") {
      // 마운트 시 바로 실행
      setTimeout(() => {
        resizeContent();
      }, 100);

      // 이벤트 리스너 등록
      window.addEventListener("resize", resizeContent);

      // 언마운트 시 정리(cleanup) - 메모리 누수 방지
      return () => {
        window.removeEventListener("resize", resizeContent);
      };
    }
  }, [resizeContent, did]); // 의존성 배열에 resizeContent 포함

  if (loading)
    return did === "1" || did === "0" ? (
      <div className="content-wrapper" ref={ref}>
        <Loading key={"loading"} />
      </div>
    ) : (
      <div className="content-wrapper" ref={ref}>
        <LoadingPortrait key={"loading-portrait"} />
      </div>
    );
  if (!isAuthenticated)
    return (
      <div className="content-wrapper" ref={ref}>
        <Login />
      </div>
    );

  const getScreen = () => {
    if (did === "0" && step === 0) {
      return <Admin key={"admin"} />;
    }

    const clubDayChunks = [];
    for (let i = 0; i < clubDayBadges.length; i += 4) {
      clubDayChunks.push(clubDayBadges.slice(i, i + 4));
    }

    if (did === "0" && step > 0 && basicProgram?.programType === "BASIC") {
      return (
        <ControlPanel key={"control-panel"} clubDayChunks={clubDayChunks} />
      );
    }
    if (did === "1" && step > 0 && basicProgram?.programType === "BASIC") {
      return <LandescapeScreen />;
    }
    const firstWeight = <PortraitDemoA key={"first-weight"} />;
    const secondWeight = <PortraitDemoB key={"second-weight"} />;

    const firstCircuit =
      did === "L1" || did === "L6" ? (
        <PortraitDemoA key={"first-circuit-a"} />
      ) : (
        <PortraitDemoB key={"first-circuit-b"} />
      );
    const secondCircuit =
      did === "L1" || did === "L6" ? (
        <PortraitDemoA key={"second-circuit-a"} />
      ) : (
        <PortraitDemoB key={"second-circuit-b"} />
      );

    const samePages = [
      did === "1" || did === "0" ? (
        <LandscapeDefault key={"landscape-default"} />
      ) : (
        <PortraitDefault key={"portrait-default"} />
      ),
      did === "1" || did === "0" ? (
        <Start key={"start"} />
      ) : (
        <PortraitDefault key={"portrait-default"} />
      ),
      did === "1" || did === "0" ? (
        <IceBreaking key={"ice-breaking"} />
      ) : (
        <PortraitDefault key={"portrait-default"} />
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={2} time={4} key={"rest1"} />
      ) : (
        <PortraitDefault key={"portrait-default"} />
      ),
      did === "1" || did === "0" ? (
        <DynamicStretching key={"dynamic-stretching"} />
      ) : (
        <PortraitDefault key={"portrait-default"} />
      ),
    ];

    const otherSamePages = [
      did === "1" || did === "0" ? (
        <CoolDown key={"cool-down"} />
      ) : (
        <PortraitDefault key={"portrait-default"} />
      ),
      ...clubDayChunks.map((chunk, index) =>
        did === "1" || did === "0" ? (
          <ClubDay
            key={`club-day-${index}`}
            data={chunk}
            index={index}
            length={clubDayChunks.length}
          />
        ) : (
          <PortraitDefault key={"portrait-default"} />
        ),
      ),
    ];

    const rowing =
      did === "1" || did === "0"
        ? [
            ...(classId === undefined
              ? []
              : [
                  <LandscapeTeamRow key={"landscape-row-team"} />,
                  <LandscapeRowTeamCheck key={"landscape-row-team-check"} />,
                ]),
            <LandscapeRow key={"landscape-row"} />,
          ]
        : [
            <PortraitRow key={"portrait-row1"} />,
            <PortraitRow key={"portrait-row2"} />,
            <PortraitRow key={"portrait-row3"} />,
          ];
    const teamWorkOuts =
      circuitProgram?.isTeamWorkOutAvailable === false ||
      weightProgram?.isTeamWorkOutAvailable === false ||
      hiluxProgram?.isTeamWorkOutAvailable === false
        ? []
        : row === undefined
          ? [
              did === "1" || did === "0" ? (
                <LandscapeTeamWorkout key={"landscape-team-workout"} />
              ) : (
                <PortraitTeamWorkout key={"portrait-team-workout"} />
              ),
            ]
          : rowing;

    const weightPages = [
      ...samePages,
      did === "1" || did === "0" ? (
        <LandscapeDemo key={"landscape-demo"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} time={4} key={"rest2"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <WorkoutType1 key={"workout-type-1"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} key={"rest3"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <WorkoutType1 secondRound key={"workout-type-1-second-round"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} key={"rest4"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <WorkoutType1 key={"workout-type-1-second"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} key={"rest5"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      did === "1" || did === "0" ? (
        <WorkoutType1 secondRound key={"workout-type-1-second-round-2"} />
      ) : did === "L1" || did === "L6" ? (
        firstWeight
      ) : (
        secondWeight
      ),
      ...teamWorkOuts,
      ...otherSamePages,
    ];

    const circuitPages = [
      ...samePages,
      did === "1" || did === "0" ? (
        <LandscapeDemo key={"landscape-demo"} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} time={4} key={"rest6"} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2"} round={1} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-second"} round={2} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-3"} round={3} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-4"} round={4} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-5"} round={5} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-6"} round={6} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} key={"rest-between-sets"} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-set2-round1"} round={1} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-set2-round2"} round={2} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-set2-round3"} round={3} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-set2-round4"} round={4} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-set2-round5"} round={5} />
      ) : (
        secondCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType2 key={"workout-type-2-set2-round6"} round={6} last />
      ) : (
        secondCircuit
      ),
      ...teamWorkOuts,
      ...otherSamePages,
    ];

    const newCircuitPages = [
      ...samePages,
      did === "1" || did === "0" ? (
        <LandscapeDemo key={"landscape-demo"} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} time={4} key={"rest8"} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType3 round={1} key={11} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType3 round={2} key={22} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType3 round={3} key={33} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} time={60} key={"rest9"} />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType3 round={1} key={21} second />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType3 round={2} key={22} second />
      ) : (
        firstCircuit
      ),
      did === "1" || did === "0" ? (
        <WorkoutType3 round={3} key={23} second />
      ) : (
        firstCircuit
      ),
      ...teamWorkOuts,
      ...otherSamePages,
    ];

    const hiluxPages = [
      ...samePages,
      did === "1" || did === "0" ? (
        <LandscapeDemo key={"landscape-demo-hilux"} />
      ) : (
        <HiluxStationPortrait key={"hilux-station-portrait-demo"} />
      ),
      ...(classId !== undefined
        ? [
            did === "0" ? (
              <HiluxTeamSetup key={"hilux-team-setup"} />
            ) : did === "1" ? (
              <LandscapeDemo key={"landscape-demo-hilux-setup"} />
            ) : (
              <HiluxStationPortrait key={"hilux-station-portrait-setup"} />
            ),
            did === "0" ? (
              <HiluxDifficulty key={"hilux-difficulty"} />
            ) : did === "1" ? (
              <LandscapeDemo key={"landscape-demo-hilux-diff"} />
            ) : (
              <HiluxStationPortrait key={"hilux-station-portrait-diff"} />
            ),
            did === "1" || did === "0" ? (
              <HiluxDifficultyCheck key={"hilux-difficulty-check"} />
            ) : (
              <HiluxStationPortrait key={"hilux-station-portrait-diff-check"} />
            ),
          ]
        : [
            did === "0" ? (
              <HiluxDifficulty key={"hilux-difficulty"} />
            ) : did === "1" ? (
              <LandscapeDemo key={"landscape-demo-hilux-diff"} />
            ) : (
              <HiluxStationPortrait key={"hilux-station-portrait-diff"} />
            ),
            did === "1" || did === "0" ? (
              <HiluxDifficultyCheck key={"hilux-difficulty-check"} />
            ) : (
              <HiluxStationPortrait key={"hilux-station-portrait-diff-check"} />
            ),
          ]),
      did === "1" || did === "0" ? (
        <Rest nowStep={4} time={4} key={"rest-hilux-2"} />
      ) : (
        <HiluxStationPortrait key={"hilux-portrait-default"} />
      ),
      did === "1" || did === "0" ? (
        <HiluxWorkout key={"hilux-workout"} />
      ) : (
        <HiluxStationPortrait key={"hilux-station-portrait"} />
      ),
      did === "1" || did === "0" ? (
        <HiluxRanking key={"hilux-ranking"} />
      ) : (
        <HiluxPortraitDefault key={"hilux-portrait-default"} />
      ),
      ...teamWorkOuts,
      ...otherSamePages,
    ];

    if (basicProgram?.programType === "BASIC") {
      return <BasicScreen key={"basic-screen"} clubDayChunks={clubDayChunks} />;
    }

    const currentPages =
      hiluxProgram?.programType === "HILUX"
        ? hiluxPages
        : circuitProgram?.programType === "BOOSTER"
          ? circuitPages
          : circuitProgram?.programType === "CIRCUIT"
            ? newCircuitPages
            : weightPages;

    if (currentPages.length <= step) {
      setStep(0);
      return did === "1" || did === "0" ? (
        <LandscapeDefault key={"landscape-default"} />
      ) : (
        <PortraitDefault key={"portrait-default"} />
      );
    }

    return currentPages[step];
  };

  return (
    <div className="content-wrapper" ref={ref}>
      {getScreen()}
      <Toaster />
    </div>
  );
}

export default App;
